import { Edit, useForm, useSelect } from "@refinedev/antd";
import { Form, Input, Select, Row, Col, Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import type { PlanningGroupFormValues, Season } from "../../types";
import { usePageTitle } from "../../hooks";

/**
 * PlanningGroupEdit Component
 *
 * Admin-side edit form: pick the audit company in charge of the planning,
 * plus name/season/dates. The list of specific auditors (`users`) is
 * managed by the company's coordinator (user_type=4) elsewhere and is
 * intentionally NOT exposed here.
 */
export const PlanningGroupEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  usePageTitle(t("planning.edit", "Editar Planificación"));

  const { formProps, saveButtonProps } = useForm<PlanningGroupFormValues>({
    resource: "planning-groups",
    action: "edit",
    redirect: "show",
  });

  const { selectProps: seasonSelectProps } = useSelect<Season>({
    resource: "seasons",
    optionLabel: "name",
    optionValue: "id",
  });

  // Endpoint lives at /api/v1/admin/audit-companies/ — resource path
  // must include the `v1/` prefix, otherwise the data provider hits
  // /api/admin/audit-companies/ and Django returns 404.
  const { selectProps: auditCompanySelectProps } = useSelect<{ id: number; name: string }>({
    resource: "v1/admin/audit-companies",
    optionLabel: "name",
    optionValue: "id",
    filters: [{ field: "is_active", operator: "eq", value: true }],
    pagination: { pageSize: 200 },
  });

  return (
    <Edit
      saveButtonProps={saveButtonProps}
      headerButtons={() => (
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          {t("buttons.back")}
        </Button>
      )}
    >
      <Form
        {...formProps}
        layout="vertical"
        onFinish={(values: any) => {
          const transformedValues = {
            ...values,
            season: typeof values.season === "object" && values.season !== null
              ? values.season.id
              : values.season,
            audit_company: typeof values.audit_company === "object" && values.audit_company !== null
              ? values.audit_company.id
              : values.audit_company,
          };
          // Dates are derived (first_audit_date / last_audit_date) and
          // no longer editable from this form — drop them so we don't
          // overwrite them.
          delete transformedValues.start_date;
          delete transformedValues.finish_date;
          // Drop `users` from the admin payload — coordinator owns it.
          delete transformedValues.users;
          return formProps.onFinish?.(transformedValues);
        }}
      >
        <Form.Item
          label={t("planning.name", "Nombre de la Planificación")}
          name="name"
          rules={[
            {
              required: true,
              message: t("planning.name_required", "Por favor ingrese el nombre de la planificación"),
            },
            {
              max: 200,
              message: t("planning.name_max", "El nombre no puede exceder 200 caracteres"),
            },
          ]}
        >
          <Input placeholder={t("planning.name", "Ingrese el nombre de la planificación")} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label={t("planning.season", "Campaña")}
              name="season"
              rules={[
                {
                  required: true,
                  message: t("planning.season_required", "Por favor seleccione una campaña"),
                },
              ]}
              getValueProps={(value) => ({
                value: typeof value === "object" && value !== null ? value.id : value,
              })}
              getValueFromEvent={(value) => value}
            >
              <Select
                {...seasonSelectProps}
                placeholder={t("planning.season", "Seleccione una campaña")}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label={t("planning.audit_company", "Empresa Auditora")}
              name="audit_company"
              tooltip={t(
                "planning.audit_company_tooltip",
                "La empresa que realizará la auditoría. Su coordinador asignará después los auditores concretos.",
              )}
              rules={[
                {
                  required: true,
                  message: `${t("planning.audit_company", "Empresa Auditora")} ${t("common.required")}`,
                },
              ]}
              getValueProps={(value) => ({
                value: typeof value === "object" && value !== null ? value.id : value,
              })}
              getValueFromEvent={(value) => value}
            >
              <Select
                {...auditCompanySelectProps}
                placeholder={t("planning.audit_company_placeholder", "Seleccione la empresa auditora")}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

      </Form>
    </Edit>
  );
};
